import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { quizApi } from '../utils/api';
import ChessDiagram from '../components/ChessDiagram';

export default function PublicQuizPage() {
  const { token } = useParams();
  const [quiz, setQuiz]         = useState(null);
  const [cards, setCards]       = useState([]);
  const [creatorPrefs, setCreatorPrefs] = useState({});
  const [idx, setIdx]           = useState(0);
  const [started, setStarted]   = useState(false);
  const [done, setDone]         = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    quizApi.getPublic(token)
      .then(data => {
        setQuiz(data.quiz);
        setCards(data.cards || []);
        setCreatorPrefs(data.creatorPrefs || {});
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const next = () => {
    if (idx + 1 >= cards.length) setDone(true);
    else setIdx(i => i + 1);
  };

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;
  if (error)   return <div className="alert alert-danger">Quiz not found or unavailable.</div>;
  if (!started) return (
    <div className="row justify-content-center">
      <div className="col-md-5 text-center">
        <h2 className="mb-2">{quiz?.title || 'Quiz'}</h2>
        <p className="text-muted mb-4">{cards.length} card{cards.length !== 1 ? 's' : ''} in this quiz.</p>
        <button className="btn btn-primary btn-lg" onClick={() => setStarted(true)}>Start Quiz</button>
      </div>
    </div>
  );

  if (done) return (
    <div className="row justify-content-center">
      <div className="col-md-5 text-center">
        <h2 className="mb-3">Quiz Complete!</h2>
        <p className="text-muted mb-4">You reviewed all {cards.length} cards.</p>
        <button className="btn btn-outline-secondary" onClick={() => { setIdx(0); setDone(false); }}>
          Restart
        </button>
      </div>
    </div>
  );

  const card = cards[idx];

  return (
    <div className="row justify-content-center">
      <div className="col-lg-7">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0 text-muted">{quiz?.title}</h5>
          <span className="text-muted small">Card {idx + 1} of {cards.length}</span>
        </div>

        <div className="progress mb-3" style={{ height: 6 }}>
          <div className="progress-bar" style={{ width: `${((idx + 1) / cards.length) * 100}%` }} />
        </div>

        <div className="card shadow">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">{card.name}</h4>
            {card.to_move && (
              <span className={`badge ${card.to_move === 'W' ? 'bg-light text-dark border' : 'bg-dark'}`}>
                {card.to_move === 'W' ? 'White to move' : 'Black to move'}
              </span>
            )}
          </div>
          <div className="card-body">
            {card.fen && (
              <div className="text-center mb-3">
                <ChessDiagram
                  fen={card.fen}
                  prefs={{
                    ...creatorPrefs,
                    diagramFlipped: card.flipped ?? creatorPrefs.diagramFlipped,
                  }}
                  size={320}
                />
              </div>
            )}
            {card.image && !card.fen && (
              <div className="text-center mb-3">
                <img src={card.image} alt="board" className="img-fluid rounded" style={{ maxHeight: 300 }} />
              </div>
            )}
            {card.problem && (
              <div>
                <h6 className="text-muted text-uppercase small mb-2">Problem</h6>
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{card.problem}</p>
              </div>
            )}
          </div>
          <div className="card-footer bg-transparent text-end">
            <button className="btn btn-primary" onClick={next}>
              {idx + 1 < cards.length ? 'Next Card →' : 'Finish'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
